import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { gmailComposeUrl, outlookComposeUrl } from "../lib/deepLinks";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import DocumentDispatchDialog from "../components/DocumentDispatchDialog";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ArrowLeft,
  Pencil,
  Loader2,
  FileText,
  FileDown,
  Mail,
  Send,
  Calendar,
  Building2,
  User,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  Trash2,
  Receipt,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatCurrency, formatDate, getStatusClass, QUOTE_STATUSES } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isOfficeManager } = useAuth();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachedFleet, setAttachedFleet] = useState([]);
  const [attachedEquipment, setAttachedEquipment] = useState([]);
  
  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [includeInternal, setIncludeInternal] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailFormat, setEmailFormat] = useState("pdf");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Email logs
  const [emailLogs, setEmailLogs] = useState([]);

  useEffect(() => {
    fetchQuote();
    fetchEmailLogs();
  }, [id]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API}/quotes/${id}`);
      setQuote(response.data);

      // iter139: hydrate attached fleet vehicles + equipment for the detail view
      const fleetIds = response.data.fleet_ids || [];
      const equipIds = response.data.equipment_ids || [];
      if (fleetIds.length || equipIds.length) {
        const [fleetRes, equipRes] = await Promise.all([
          fleetIds.length ? axios.get(`${API}/fleet/vehicles`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
          equipIds.length ? axios.get(`${API}/equipment`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        setAttachedFleet((fleetRes.data || []).filter((v) => fleetIds.includes(v.id)));
        setAttachedEquipment((equipRes.data || []).filter((e) => equipIds.includes(e.id)));
      }

      // Pre-fill email recipient
      if (response.data.property_manager_name) {
        // Get PM email
        const pmRes = await axios.get(`${API}/property-managers`);
        const pm = pmRes.data.find(p => p.id === response.data.property_manager_id);
        if (pm) {
          setRecipientEmail(pm.email);
        }
      }
    } catch (error) {
      toast.error("Failed to fetch quote");
      navigate("/quotes");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuote = async () => {
    try {
      await axios.delete(`${API}/quotes/${id}`);
      toast.success("Quote deleted");
      navigate("/quotes");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete quote");
    }
  };

  const fetchEmailLogs = async () => {
    // Phase 19.13: /api/email-logs endpoint not yet implemented backend-side.
    // Setting empty array silently avoids 404 toast/console spam on every page load.
    setEmailLogs([]);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.post(
        `${API}/quotes/${id}/export`,
        {
          format: exportFormat,
          include_internal: includeInternal,
        },
        { responseType: "blob" }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Quote_${quote.quote_number}.${exportFormat}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Quote exported as ${exportFormat.toUpperCase()}`);
      setExportDialogOpen(false);
    } catch (error) {
      toast.error("Failed to export quote");
    } finally {
      setExporting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      toast.error("Please enter a recipient email");
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/quotes/${id}/send-email`, {
        format: emailFormat,
        recipient_email: recipientEmail,
        subject: emailSubject || undefined,
        message: emailMessage || undefined,
        include_internal: false,
      });
      
      toast.success(`Quote sent to ${recipientEmail}`);
      setEmailDialogOpen(false);
      fetchQuote(); // Refresh to get updated status
      fetchEmailLogs(); // Refresh email logs
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await axios.patch(`${API}/quotes/${id}/status?status=${newStatus}`);
      
      // Check if invoice was auto-created
      if (response.data.invoice_created) {
        toast.success(
          `Status updated to ${newStatus}. Invoice ${response.data.invoice_created.invoice_number} was automatically created!`,
          { duration: 5000 }
        );
      } else {
        toast.success(`Status updated to ${newStatus}`);
      }
      
      setQuote({ ...quote, status: newStatus });
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!quote) {
    return null;
  }

  const externalServices = quote.items || [];
  const internalServices = [];
  const totalExternal = externalServices.reduce((sum, s) => sum + (s.total_price || s.total || 0), 0);

  return (
    <div className="space-y-6 animate-in" data-testid="quote-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/quotes")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold">
                {quote.quote_number}
              </h1>
              <Badge variant="outline" className={getStatusClass(quote.status)}>
                {quote.status}
              </Badge>
            </div>
            <p className="text-muted-foreground">{quote.property_address}</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Status Change */}
          {isOfficeManager && (
            <Select value={quote.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-36" data-testid="status-change-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUOTE_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Export Button */}
          <Button
            variant="outline"
            onClick={() => setExportDialogOpen(true)}
            data-testid="export-quote-btn"
          >
            <FileDown className="h-4 w-4 mr-2" />
            Export
          </Button>
          
          {/* Send Email Button */}
          <Button
            variant="outline"
            onClick={() => setEmailDialogOpen(true)}
            data-testid="send-email-btn"
          >
            <Mail className="h-4 w-4 mr-2" />
            Send
          </Button>

          {/* Phase B.5 — Unified dispatch */}
          <Button
            onClick={() => setDispatchOpen(true)}
            data-testid="dispatch-quote-btn"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Mail className="h-4 w-4 mr-2" />
            Send &amp; File
          </Button>
          <DocumentDispatchDialog kind="quote" docId={id} open={dispatchOpen} onOpenChange={setDispatchOpen} />
          
          {/* Edit Button */}
          <Link to={`/quotes/${id}/edit`}>
            <Button data-testid="edit-quote-btn">
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </Link>
          {/* Phase 19.18: Live Editor with split-pane preview */}
          {isOfficeManager && (
            <Link to={`/quotes/${id}/editor`}>
              <Button variant="outline" data-testid="live-editor-btn">
                <Pencil className="h-4 w-4 mr-2" />
                Live Editor
              </Button>
            </Link>
          )}

          {/* Convert to Invoice — Phase 19.13 (auto-creates invoice on completed status) */}
          {isOfficeManager && quote.status !== "completed" && (
            <Button
              variant="default"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => handleStatusChange("completed")}
              data-testid="convert-to-invoice-btn"
            >
              <Receipt className="h-4 w-4 mr-2" />
              Convert to Invoice
            </Button>
          )}
          
          {/* Delete Button */}
          <Button
            variant="destructive"
            onClick={() => setDeleteConfirmOpen(true)}
            data-testid="delete-quote-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Property Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Property Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-muted-foreground">Address</Label>
                <p className="font-medium">{quote.property_address}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Strata Plan</Label>
                <p className="font-mono">{quote.strata_plan_number || "N/A"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Floors</Label>
                <p>{quote.num_floors}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Units</Label>
                <p>{quote.num_units}</p>
              </div>
            </CardContent>
          </Card>

          {/* Services */}
          <Card>
            <CardHeader>
              <CardTitle>Services</CardTitle>
              <CardDescription>
                {externalServices.length} item(s) • Total: {formatCurrency(totalExternal)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service</TableHead>
                    <TableHead className="text-center">Qty</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {externalServices.map((svc, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">
                        {svc.description || svc.service_name}
                        {svc.notes && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {svc.notes}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-center">{svc.quantity}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(svc.unit_price || svc.rate)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(svc.total_price || svc.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2">
                    <TableCell colSpan={3} className="text-right font-bold">
                      Total
                    </TableCell>
                    <TableCell className="text-right font-bold text-lg">
                      {formatCurrency(totalExternal)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              
              {/* Internal Services (only visible to office manager) */}
              {isOfficeManager && internalServices.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm font-medium text-muted-foreground mb-4">
                    Internal Items (not shown on client quote)
                  </p>
                  <Table>
                    <TableBody>
                      {internalServices.map((svc, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">
                            {svc.service_name}
                          </TableCell>
                          <TableCell className="text-center">{svc.quantity}</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(svc.rate)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(svc.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fleet & Equipment (iter139) */}
          {(attachedFleet.length > 0 || attachedEquipment.length > 0) && (
            <Card data-testid="quote-fleet-equipment-card">
              <CardHeader>
                <CardTitle>Fleet & Equipment</CardTitle>
                <CardDescription>
                  {attachedFleet.length} vehicle(s), {attachedEquipment.length} equipment item(s)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {attachedFleet.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground">Vehicles</Label>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      {attachedFleet.map((v) => (
                        <div key={v.id} className="rounded-lg border bg-muted/30 p-3" data-testid={`quote-fleet-row-${v.id}`}>
                          <p className="font-medium text-sm">
                            {v.name || [v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {v.license_plate || v.vin || v.type || "—"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {attachedEquipment.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground">Equipment</Label>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      {attachedEquipment.map((e) => (
                        <div key={e.id} className="rounded-lg border bg-muted/30 p-3" data-testid={`quote-equipment-row-${e.id}`}>
                          <p className="font-medium text-sm">{e.name || "Equipment"}</p>
                          <p className="text-xs text-muted-foreground">
                            {e.serial_number || e.category || "—"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Job Details */}
          <Card>
            <CardHeader>
              <CardTitle>Job Details</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-muted-foreground">Job Types</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {quote.job_types?.map((type) => (
                    <Badge key={type} variant="secondary">
                      {type.replace("_", " ")}
                    </Badge>
                  )) || <span className="text-muted-foreground">None specified</span>}
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Piecework Hours</Label>
                <p>{quote.piecework_hours || "N/A"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Quote Ratio</Label>
                <p>{quote.quote_ratio || "N/A"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Actual Hours</Label>
                <p>{quote.actual_hours || "N/A"}</p>
              </div>
              {quote.safety_hazards && (
                <div className="sm:col-span-2">
                  <Label className="text-muted-foreground">Safety Hazards</Label>
                  <p className="text-amber-600 dark:text-amber-400">
                    {quote.safety_hazards}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {(quote.external_notes || quote.internal_notes) && (
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {quote.external_notes && (
                  <div>
                    <Label className="text-muted-foreground">Client Notes</Label>
                    <p className="mt-1">{quote.external_notes}</p>
                  </div>
                )}
                {isOfficeManager && quote.internal_notes && (
                  <div>
                    <Label className="text-muted-foreground">Internal Notes</Label>
                    <p className="mt-1 text-muted-foreground italic">
                      {quote.internal_notes}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Client Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Client
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Company</Label>
                <p className="font-medium">{quote.company_name}</p>
              </div>
              {quote.property_manager_name && (
                <div>
                  <Label className="text-muted-foreground">Property Manager</Label>
                  <p>{quote.property_manager_name}</p>
                </div>
              )}
              {quote.on_site_contact && (
                <div>
                  <Label className="text-muted-foreground">On-Site Contact</Label>
                  <p>{quote.on_site_contact}</p>
                </div>
              )}
              {quote.access_info && (
                <div>
                  <Label className="text-muted-foreground">Access Info</Label>
                  <p className="text-sm">{quote.access_info}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Quoted</span>
                <span>{formatDate(quote.date_quoted)}</span>
              </div>
              {quote.date_sent && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sent</span>
                  <span>{formatDate(quote.date_sent)}</span>
                </div>
              )}
              {quote.date_approved && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Approved</span>
                  <span>{formatDate(quote.date_approved)}</span>
                </div>
              )}
              {quote.date_completed && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Completed</span>
                  <span>{formatDate(quote.date_completed)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quoter Info */}
          {quote.quoter_name && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Quoter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p>{quote.quoter_name}</p>
              </CardContent>
            </Card>
          )}

          {/* Email History */}
          {emailLogs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email History
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {emailLogs.slice(0, 5).map((log) => (
                  <div key={log.id} className="text-sm border-b pb-2 last:border-0">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                      <span className="font-medium truncate">{log.recipient}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(log.sent_at)} • {log.attachment_format.toUpperCase()}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Quote</DialogTitle>
            <DialogDescription>
              Download the quote in your preferred format
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger data-testid="export-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      PDF Document
                    </div>
                  </SelectItem>
                  <SelectItem value="docx">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Word Document (DOCX)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {isOfficeManager && internalServices.length > 0 && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include-internal"
                  checked={includeInternal}
                  onCheckedChange={setIncludeInternal}
                />
                <Label htmlFor="include-internal">
                  Include internal items (office use only)
                </Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExport} disabled={exporting} data-testid="confirm-export-btn">
              {exporting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <FileDown className="h-4 w-4 mr-2" />
              Download {exportFormat.toUpperCase()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Quote via Email</DialogTitle>
            <DialogDescription>
              Send the quote directly to your client
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Recipient Email *</Label>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="client@example.com"
                data-testid="email-recipient-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Attachment Format</Label>
              <Select value={emailFormat} onValueChange={setEmailFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF Document</SelectItem>
                  <SelectItem value="docx">Word Document (DOCX)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Subject (Optional)</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder={`Quote ${quote.quote_number} - ${quote.property_address}`}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message (Optional)</Label>
              <Textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Add a personalized message..."
                rows={4}
              />
            </div>
            
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Email sending is currently <strong>mocked</strong>. The email details will be logged but not actually sent.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 mr-auto">
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="open-in-gmail-btn"
                disabled={!recipientEmail}
                onClick={() => {
                  const subject = emailSubject || `Quote ${quote.quote_number} - ${quote.property_address}`;
                  const body = emailMessage || `Hello,\n\nPlease find the details for quote ${quote.quote_number} attached.\n\nThanks.`;
                  window.open(gmailComposeUrl({ to: recipientEmail, subject, body }), "_blank", "noopener,noreferrer");
                }}
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Open in Gmail
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="open-in-outlook-btn"
                disabled={!recipientEmail}
                onClick={() => {
                  const subject = emailSubject || `Quote ${quote.quote_number} - ${quote.property_address}`;
                  const body = emailMessage || `Hello,\n\nPlease find the details for quote ${quote.quote_number} attached.\n\nThanks.`;
                  window.open(outlookComposeUrl({ to: recipientEmail, subject, body }), "_blank", "noopener,noreferrer");
                }}
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Open in Outlook
              </Button>
            </div>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sending || !recipientEmail} data-testid="confirm-send-btn">
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Send className="h-4 w-4 mr-2" />
              Send Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Quote</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete quote {quote?.quote_number}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteQuote} data-testid="confirm-delete-quote-btn">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteDetail;
